import { type ZodTypeAny, z } from 'zod';

import { isDefined, oneOrMany } from '../../../utils.js';
import { type BaseItem } from '../../common.js';

export const ScheduledFlowTriggerFindAllOptionsSchema = z.object({
  flowId: z.uuid().optional(),
  flowDefinitionId: z.uuid().optional(),
  organizationId: oneOrMany(z.uuid()).optional(),
  isActive: z.coerce.boolean().optional(),
  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
  orderBy: z.string().optional(),
  orderDirection: z.enum(['asc', 'desc']).optional(),
});
export type FindAllOptions = z.infer<typeof ScheduledFlowTriggerFindAllOptionsSchema>;

// Overlap Policy (matches Temporal ScheduleOverlapPolicy)
export enum ScheduleOverlapPolicy {
  /** Don't start a new Action if the previous is still running */
  Skip = 'SKIP',
  /** Buffer one Action to run after the current completes */
  BufferOne = 'BUFFER_ONE',
  /** Buffer all Actions to run sequentially */
  BufferAll = 'BUFFER_ALL',
  /** Cancel the running Action, then start the new one */
  CancelOther = 'CANCEL_OTHER',
  /** Terminate the running Action immediately, start the new one */
  TerminateOther = 'TERMINATE_OTHER',
  /** Allow any number of Actions to run concurrently */
  AllowAll = 'ALLOW_ALL',
}

export const ScheduleOverlapPolicySchema = z.nativeEnum(ScheduleOverlapPolicy);

// ============================================================================
// Schedule Spec Schemas (derived from Temporal's types)
// ============================================================================

// Month and day of week constants (matching Temporal's exported constants)
const MONTHS = [
  'JANUARY',
  'FEBRUARY',
  'MARCH',
  'APRIL',
  'MAY',
  'JUNE',
  'JULY',
  'AUGUST',
  'SEPTEMBER',
  'OCTOBER',
  'NOVEMBER',
  'DECEMBER',
] as const;

const DAYS_OF_WEEK = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
] as const;

// Unit schemas derived from the constants above
const MonthSchema = z.enum(MONTHS);
const DayOfWeekSchema = z.enum(DAYS_OF_WEEK);

// Duration type (matches Temporal's Duration: string like "1h", "30m" or number in ms)
type TemporalDuration = string | number;

const TemporalDurationSchema = z.custom<TemporalDuration>(
  (val) => typeof val === 'number' || typeof val === 'string'
);

/**
 * Factory for Temporal's Range<Unit>:
 * { start: Unit, end: Unit, step: number }
 */
function createRangeSchema<T extends ZodTypeAny>(unitSchema: T) {
  return z.object({
    start: unitSchema,
    end: unitSchema,
    step: z.number(),
  });
}

/**
 * Factory for Temporal's LooseRange<Unit>:
 * Range<Unit> | { start: Unit, end?: Unit } | Unit
 *
 * Note: The { step?: never } constraint means step should not be present.
 * We handle this by using .strict() which rejects extra keys.
 */
function createLooseRangeSchema<T extends ZodTypeAny>(unitSchema: T) {
  return z.union([
    unitSchema, // Just the unit value itself
    createRangeSchema(unitSchema), // Full Range<Unit> with step
    z
      .object({
        start: unitSchema,
        end: unitSchema.optional(),
      })
      .strict(), // .strict() rejects unknown keys like 'step'
  ]);
}

/**
 * Factory for calendar fields: '*' | LooseRange<T> | LooseRange<T>[]
 */
function createCalendarFieldSchema<T extends ZodTypeAny>(unitSchema: T) {
  const looseRange = createLooseRangeSchema(unitSchema);
  return z.union([z.literal('*'), looseRange, z.array(looseRange)]).optional();
}

// Build the CalendarSpec schema with correct unit types for each field
const CalendarSpecSchema = z.object({
  second: createCalendarFieldSchema(z.number()),
  minute: createCalendarFieldSchema(z.number()),
  hour: createCalendarFieldSchema(z.number()),
  dayOfMonth: createCalendarFieldSchema(z.number()),
  month: createCalendarFieldSchema(MonthSchema),
  year: createCalendarFieldSchema(z.number()),
  dayOfWeek: createCalendarFieldSchema(DayOfWeekSchema),
  comment: z.string().optional(),
});

const IntervalSpecSchema = z.object({
  /** Duration: number of milliseconds or ms-formatted string */
  every: TemporalDurationSchema,
  /** Offset: number of milliseconds or ms-formatted string */
  offset: TemporalDurationSchema.optional(),
});

export const ScheduleSpecSchema = z
  .object({
    /** Calendar-based specifications (e.g., "every Monday at 9am") */
    calendars: z.array(CalendarSpecSchema).optional(),
    /** Interval-based specifications (e.g., "every 4 hours") */
    intervals: z.array(IntervalSpecSchema).optional(),
    /** Cron expressions (e.g., "0 9 * * 1-5") */
    cronExpressions: z.array(z.string()).optional(),
    /** Times to skip (e.g., holidays) */
    skip: z.array(CalendarSpecSchema).optional(),
    /** Don't run before this date (ISO string) */
    startAt: z.string().datetime().optional(),
    /** Don't run after this date (ISO string) */
    endAt: z.string().datetime().optional(),
    /** Random delay: number of milliseconds or ms-formatted string */
    jitter: TemporalDurationSchema.optional(),
    /** IANA timezone (e.g., "America/Los_Angeles") */
    timezone: z.string().optional(),
  })
  .refine(
    (spec) =>
      (isDefined(spec.calendars) && spec.calendars.length > 0) ||
      (isDefined(spec.intervals) && spec.intervals.length > 0) ||
      (isDefined(spec.cronExpressions) && spec.cronExpressions.length > 0),
    { message: 'At least one of calendars, intervals, or cronExpressions must be provided' }
  );

export type ScheduleSpec = z.infer<typeof ScheduleSpecSchema>;
export type CalendarSpec = z.infer<typeof CalendarSpecSchema>;
export type IntervalSpec = z.infer<typeof IntervalSpecSchema>;

// ============================================================================
// Flow Initiator Config (shared concept with event triggers)
// ============================================================================

/**
 * Configuration shared by all mechanisms that can initiate flow runs.
 * Uses composition rather than inheritance.
 */
export interface FlowInitiatorConfig {
  flowId: string;
  organizationId: string;
  /** Rate limit for flow runs triggered by this initiator */
  maxFlowRunsPerMinute: number | null;
  /** Whether to allow running unpublished flow versions */
  allowUnpublishedFlowRuns: boolean | null;
  /** Priority level for flow runs created by this initiator */
  desiredFlowRunPriority: number | null;
}

// ============================================================================
// Schedule Flow Trigger Interface
// ============================================================================

/**
 * Base fields specific to scheduled flow triggers.
 */
export interface BaseScheduledFlowTrigger {
  /** The specific FlowDefinition version this trigger is pinned to. */
  flowDefinitionId: string;
  /** Full schedule specification (Temporal ScheduleSpec format) */
  scheduleSpec: ScheduleSpec;
  /** Human-readable name */
  name: string;
  /** Description of the schedule */
  description: string | null;
  /** Whether the schedule is active */
  isActive: boolean;
  /** What to do when runs overlap */
  overlapPolicy: ScheduleOverlapPolicy;
  /** How far back to catch up missed runs (milliseconds). Technically this can be a Temporal Duration,
   * but for consistency in pulling from OUR DB, we're just tracking in ms
   */
  catchupWindowMs: number;
  /** Pause the schedule if an execution fails */
  pauseOnFailure: boolean;
  /** Reference to the Temporal schedule */
  temporalScheduleId: string | null;
}

/**
 * Full scheduled flow trigger record.
 * Composes BaseItem, FlowInitiatorConfig, and schedule-specific fields.
 *
 * Note: Unlike event triggers, scheduled triggers are a standalone concept
 * and do not share a type hierarchy with event-based triggers.
 */
export interface ScheduledFlowTrigger
  extends BaseItem, FlowInitiatorConfig, BaseScheduledFlowTrigger {}

// Create schema - omit auto-generated and Temporal-synced fields
const CreateScheduledFlowTriggerSchema = z.object({
  flowId: z.uuid(),
  flowDefinitionId: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  scheduleSpec: ScheduleSpecSchema,
  overlapPolicy: ScheduleOverlapPolicySchema.optional(),
  catchupWindowMs: z.coerce.number().nonnegative().optional(),
  pauseOnFailure: z.coerce.boolean().optional(),
  isActive: z.coerce.boolean().optional(),

  // Base trigger fields
  maxFlowRunsPerMinute: z.coerce.number().positive().nullable().optional(),
  allowUnpublishedFlowRuns: z.coerce.boolean().nullable().optional(),
  desiredFlowRunPriority: z.coerce.number().nullable().optional(),
});

// Update schema - cannot change flowId, flowDefinitionId, or organizationId
const _UpdateScheduledFlowTriggerSchema = CreateScheduledFlowTriggerSchema.omit({
  flowId: true,
  flowDefinitionId: true,
  organizationId: true,
}).partial();

export type CreateScheduledFlowTrigger = z.infer<typeof CreateScheduledFlowTriggerSchema>;
export type UpdateScheduledFlowTrigger = z.infer<typeof _UpdateScheduledFlowTriggerSchema>;
